import { ColGroupDef } from '@ag-grid-community/core';
import {
  AgGridHelperService,
  ProductColGroupId,
  ProductColId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, SuppressDetails } from '@types';
import {
  FundListingPerformanceController,
  FundListingRatingsController,
} from '../controllers';

/**
 * Return default performance column definitions for BR.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultPerformanceColumnDefsForBR(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      children: [
        agGridHelperService.getFundNameColDef(
          false,
          {},
          listingController.productType,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController, {
          width: 180,
          wrapText: false,
        }),
        agGridHelperService.getNavChangePercentageColDef(listingController, {
          maxWidth: 170,
          width: 145,
          wrapText: false,
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          listingController,
          suppressDetails,
          {
            width: 70,
          }
        ),
        agGridHelperService.getCumulativeMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 65,
          }
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(listingController, {
          width: 65,
        }),
        agGridHelperService.getCumulativeMonthly5YrColDef(listingController, {
          width: 65,
        }),
        agGridHelperService.getCumulativeMonthly10YrColDef(listingController, {
          width: 65,
        }),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          { width: 125 }
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          listingController,
          suppressDetails,
          {
            width: 70,
          }
        ),
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 65,
          }
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(listingController, {
          width: 65,
        }),
        agGridHelperService.getCumulativeQuarterly5YrColDef(listingController, {
          width: 65,
        }),
        agGridHelperService.getCumulativeQuarterly10YrColDef(
          listingController,
          { width: 65 }
        ),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          { width: 125 }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.AUM,
      children: [
        agGridHelperService.getAvg12MonthsAum({
          width: 170,
          wrapText: false,
        }),
        agGridHelperService.getFundDocumentColDef({
          headerName:
            listingController.productType === ConfigurationId.TIF
              ? translateService.instant('products.product-profile')
              : translateService.instant('products.fact-sheet'),
          width: 80,
        }),
      ],
    },
  ];
  return colGroupDefs;
}

/**
 * Return default rating column definitions for BR(Brazil).
 * @param listingController ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultRatingColumnDefsForBR(
  listingController: FundListingRatingsController,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: ProductColGroupId.FUND,
      children: [agGridHelperService.getFundNameColDef()],
    },
    {
      groupId: ProductColGroupId.CURRENCY,
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      headerClass: 'ag-header-group-cell--noflex',
      children: [
        agGridHelperService.getMorningStarCategoryColDef(listingController),
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController,
          {
            minWidth: 380,
          }
        ),
      ],
    },
  ];
}
